/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
/**
 * Who gets notified by default, and who never gets notified.
 *
 * These rules used to live in the UI, which made them the de facto source of truth: the server
 * marks `created`, `new_version` and `inputs_reviewed` as user-customizable in
 * `eventToNotifAudienceMap` and then accepts whatever audience the client sends. Keeping the policy
 * here puts it next to the role registry and the rest of the notification mappings, and makes it
 * testable without a browser.
 */
// lodash must be imported as a default here; named imports from it do not survive the ESM build.
import _ from 'lodash';
import { BidRequestInputApprovalStatus, } from '../models';
import { fromObjectEntries } from '../utils';
/**
 * Whether a notification recipient is the person whose action triggered it. Nobody is notified of
 * their own action.
 *
 * Compared on email because that is the stable identity; display names and nicknames are not
 * unique. The server derives a nickname from the email local part (`auth.ts`), so this matches the
 * comparison it has always made, except that it no longer treats two people who share a local part
 * across domains as the same person.
 */
export const isNotificationActor = (recipientEmail, actorEmail) => Boolean(recipientEmail) && recipientEmail === actorEmail;
/**
 * When an approver requests changes, the roles that need to act on those changes.
 * Keyed by the approval being given, not by the role giving it.
 */
export const defaultNotifsForChangesRequested = {
    txm_approval: ['origination_lead'],
    dev_approval: ['origination_lead'],
    procurement_approval: ['dev_lead', 'dev_engineer'],
    eng_approval: ['dev_lead'],
    finance_approval: ['dev_lead', 'dev_engineer', 'bess_lead'],
};
/** The approvals that must all land before finance can price the bid request. */
export const preFinanceApprovals = [
    'dev_approval',
    'txm_approval',
    'procurement_approval',
    'eng_approval',
];
/**
 * The roles pre-selected in the review dialog's notification list.
 *
 * On approval, nobody is notified until the approval being given completes the pre-finance set, at
 * which point finance is told the inputs are ready to price. On changes requested, the roles that
 * own the inputs in question are notified.
 */
export const getDefaultNotifsForApproval = (defaultApproval, status, bidRequestRecord) => {
    switch (status) {
        case BidRequestInputApprovalStatus.APPROVED: {
            // Finance approves last, so its approval never unblocks anyone else.
            if (defaultApproval === 'finance_approval') {
                return;
            }
            const allApprovals = {
                ..._.pick(bidRequestRecord, ...preFinanceApprovals),
                [defaultApproval]: status,
            };
            const allApproved = fromObjectEntries(preFinanceApprovals.map((approval) => [approval, BidRequestInputApprovalStatus.APPROVED]));
            if (_.isEqual(allApprovals, allApproved)) {
                return ['finance_lead'];
            }
            // Procurement inputs feed the engineering cost analysis, so approving procurement hands off to
            // engineering — notify them unless engineering is already done.
            if (defaultApproval === 'procurement_approval' &&
                bidRequestRecord?.eng_approval !== BidRequestInputApprovalStatus.APPROVED) {
                return ['dev_engineer', 'bess_lead'];
            }
            return;
        }
        case BidRequestInputApprovalStatus.CHANGES_REQUESTED:
            return defaultNotifsForChangesRequested[defaultApproval];
        default:
            return;
    }
};
//# sourceMappingURL=notification-defaults.js.map